import { ModuleId, type ModuleIds } from "@xmiles/holopix-types";
import type { AiSeedanceParams } from "./types";

// ============================================================
// 类型定义（独立于画布端，避免跨 .vue 文件导入类型）
// ============================================================

/** AI 创作视频模式 */
export type VideoModeValue = "reference" | "image-to-video" | "start-end" | "text-to-video" | "multimodal";

/** 视频清晰度 */
export type VideoQuality = "480p" | "720p" | "1080p";

/** AI 创作视频模式选项 */
export interface AiVideoModeOption {
  value: VideoModeValue;
  label: string;
  icon: string;
}

/** AI 创作视频模式能力配置 */
export interface AiVideoModeCapability {
  mode: VideoModeValue;
  maxImages: number;
  maxVideos: number;
  maxImageSizeMb: number;
  maxVideoSizeMb: number;
  ratioOptions: { value: string; label: string; iconWidth: number; iconHeight: number }[];
  qualityOptions: { value: VideoQuality; label: string }[];
  durationOptions: number[];
  defaultRatio: string;
  defaultQuality: VideoQuality;
  defaultDuration: number;
}

// ============================================================
// 常量配置
// ============================================================

export const AI_VIDEO_MODE_OPTIONS: AiVideoModeOption[] = [
  { value: "text-to-video", label: "文生", icon: "icon-a-centraliconsystem3" },
  { value: "image-to-video", label: "图生", icon: "icon-a-centraliconsystem31" },
  { value: "start-end", label: "首尾帧", icon: "icon-a-svgexport-511" },
  { value: "reference", label: "全能", icon: "icon-fengfuxijie" },
];

const RATIO_OPTIONS = [
  { value: "1:1", label: "1:1", iconWidth: 16, iconHeight: 16 },
  { value: "4:3", label: "4:3", iconWidth: 20, iconHeight: 16 },
  { value: "3:4", label: "3:4", iconWidth: 16, iconHeight: 20 },
  { value: "16:9", label: "16:9", iconWidth: 24, iconHeight: 14 },
  { value: "9:16", label: "9:16", iconWidth: 14, iconHeight: 24 },
  { value: "21:9", label: "21:9", iconWidth: 24, iconHeight: 10 },
];

const QUALITY_OPTIONS = [
  { value: "480p" as VideoQuality, label: "480p" },
  { value: "720p" as VideoQuality, label: "720p" },
  { value: "1080p" as VideoQuality, label: "1080p" },
];

function createDurationRange(min: number, max: number): number[] {
  const result: number[] = [];
  for (let i = min; i <= max; i++) result.push(i);
  return result;
}

export const AI_VIDEO_MODE_CAPABILITIES: AiVideoModeCapability[] = [
  {
    mode: "reference",
    maxImages: 9,
    maxVideos: 3,
    maxImageSizeMb: 30,
    maxVideoSizeMb: 50,
    ratioOptions: RATIO_OPTIONS,
    qualityOptions: QUALITY_OPTIONS,
    durationOptions: createDurationRange(4, 15),
    defaultRatio: "16:9",
    defaultQuality: "480p",
    defaultDuration: 5,
  },
  {
    mode: "image-to-video",
    maxImages: 9,
    maxVideos: 0,
    maxImageSizeMb: 30,
    maxVideoSizeMb: 0,
    ratioOptions: RATIO_OPTIONS,
    qualityOptions: QUALITY_OPTIONS,
    durationOptions: createDurationRange(4, 15),
    defaultRatio: "16:9",
    defaultQuality: "480p",
    defaultDuration: 5,
  },
  {
    mode: "start-end",
    maxImages: 2,
    maxVideos: 0,
    maxImageSizeMb: 30,
    maxVideoSizeMb: 0,
    ratioOptions: RATIO_OPTIONS,
    qualityOptions: QUALITY_OPTIONS,
    durationOptions: createDurationRange(4, 15),
    defaultRatio: "16:9",
    defaultQuality: "480p",
    defaultDuration: 5,
  },
  {
    mode: "text-to-video",
    maxImages: 0,
    maxVideos: 0,
    maxImageSizeMb: 0,
    maxVideoSizeMb: 0,
    ratioOptions: RATIO_OPTIONS,
    qualityOptions: QUALITY_OPTIONS,
    durationOptions: createDurationRange(4, 15),
    defaultRatio: "16:9",
    defaultQuality: "480p",
    defaultDuration: 5,
  },
];

export function getAiVideoModeCapability(mode: VideoModeValue): AiVideoModeCapability {
  return AI_VIDEO_MODE_CAPABILITIES.find((item) => item.mode === mode) ?? AI_VIDEO_MODE_CAPABILITIES[0];
}

// ============================================================
// AI 创作视频模块 ID 映射（模型 × 模式 → 模块 ID）
// 2.0: 75-78 / Mini: 79-82，与画布端 55-57/64 和 69-72 独立
// ============================================================

/**
 * AI 创作视频模块 ID 注册表（二维）：模型 key × 模式 → 模块 ID
 *
 * 与画布端 VIDEO_GENERATE_MODULE_MAP 平行，使用 AI 创作专属模块 ID。
 */
const AI_VIDEO_GENERATE_MODULE_MAP: Record<string, Record<VideoModeValue, ModuleIds>> = {
  "seedance-2-0": {
    "reference": ModuleId.AiSeedanceReferenceVideo,
    "start-end": ModuleId.AiSeedanceStartEndVideo,
    "image-to-video": ModuleId.AiSeedanceImage2Video,
    "text-to-video": ModuleId.AiSeedanceText2Video,
    "multimodal": ModuleId.AiSeedanceReferenceVideo,
  },
  "seedance-mini": {
    "reference": ModuleId.AiSeedanceMiniReferenceVideo as ModuleIds,
    "start-end": ModuleId.AiSeedanceMiniStartEndVideo as ModuleIds,
    "image-to-video": ModuleId.AiSeedanceMiniImage2Video as ModuleIds,
    "text-to-video": ModuleId.AiSeedanceMiniText2Video as ModuleIds,
    "multimodal": ModuleId.AiSeedanceMiniReferenceVideo as ModuleIds,
  },
};

/** AI 创作视频默认模式，可配置化修改（FR-002a） */
export const AI_VIDEO_DEFAULT_MODE: VideoModeValue = "reference";

/** AI 创作视频默认模型 key */
export const AI_VIDEO_DEFAULT_MODEL_KEY = "seedance-2-0";

/**
 * 根据模式 + 模型确定目标模块 ID。
 * @param mode 视频模式
 * @param modelKey 模型标识，默认 "seedance-2-0"
 */
export function getAiVideoModuleId(mode: VideoModeValue, modelKey: string = AI_VIDEO_DEFAULT_MODEL_KEY): ModuleIds {
  return AI_VIDEO_GENERATE_MODULE_MAP[modelKey]?.[mode]
    ?? AI_VIDEO_GENERATE_MODULE_MAP[AI_VIDEO_DEFAULT_MODEL_KEY].reference;
}

/**
 * 根据模块 ID 反解析模式（编辑复用时从历史记录恢复面板模式）
 * 75/79 → start-end / 76/80 → reference / 77/81 → image-to-video / 78/82 → text-to-video
 */
export function resolveAiVideoModeByModuleId(moduleId?: string | number | null): VideoModeValue {
  const id = Number(moduleId);
  if (id === Number(ModuleId.AiSeedanceStartEndVideo) || id === Number(ModuleId.AiSeedanceMiniStartEndVideo)) return "start-end";
  if (id === Number(ModuleId.AiSeedanceReferenceVideo) || id === Number(ModuleId.AiSeedanceMiniReferenceVideo)) return "reference";
  if (id === Number(ModuleId.AiSeedanceImage2Video) || id === Number(ModuleId.AiSeedanceMiniImage2Video)) return "image-to-video";
  if (id === Number(ModuleId.AiSeedanceText2Video) || id === Number(ModuleId.AiSeedanceMiniText2Video)) return "text-to-video";
  return AI_VIDEO_DEFAULT_MODE;
}

/**
 * 根据模块 ID 反解析模型 key（编辑复用时从历史记录恢复模型选择）
 * 79-82 → "seedance-mini"，其他 → "seedance-2-0"
 */
export function resolveAiVideoModelKeyByModuleId(moduleId?: string | number | null): string {
  const id = Number(moduleId);
  const MINI_IDS = [
    ModuleId.AiSeedanceMiniStartEndVideo,
    ModuleId.AiSeedanceMiniReferenceVideo,
    ModuleId.AiSeedanceMiniImage2Video,
    ModuleId.AiSeedanceMiniText2Video,
  ];
  if (MINI_IDS.includes(id as any)) return "seedance-mini";
  return "seedance-2-0";
}

/**
 * 将历史记录中的 API 格式参数映射为面板 AiSeedanceParams 格式。
 *
 * 历史记录保存的是提交给后端的请求参数（videoMode/videoResolution/videoRatio/
 * imageList/firstImageUrl 等），面板读取的是 AiSeedanceParams（mode/quality/ratio/
 * referenceImages/firstFrame 等），字段名不匹配直接 resetModuleParams 会导致面板读到默认值。
 *
 * @param recordParams 历史记录中的原始参数（API 格式）
 * @returns 可直接合并到 artworkStore.aivideo.params 的 patch
 */
export function mapAiSeedanceRecordToParams(recordParams: any): Partial<AiSeedanceParams> {
  if (!recordParams || typeof recordParams !== "object") return {};

  // 兼容两种来源：新模块 75-78 / 79-82 的 param.module 即原始模块 ID
  const modelKey = resolveAiVideoModelKeyByModuleId(recordParams.module);
  const mode = resolveAiVideoModeByModuleId(recordParams.module);
  const newModuleId = getAiVideoModuleId(mode, modelKey);

  const patch: Partial<AiSeedanceParams> = {
    module: newModuleId,
    mode,
    modelKey,
    prompt: recordParams.prompt || "",
    quality: recordParams.videoResolution || recordParams.quality || "480p",
    // 图生/首尾帧提交 "adaptive"，真实比例在 calcRatio 中，面板展示需用具体值
    ratio: recordParams.videoRatio && recordParams.videoRatio !== "adaptive"
      ? recordParams.videoRatio
      : (recordParams.calcRatio || recordParams.ratio || "16:9"),
    duration: recordParams.videoDuration || recordParams.duration || 5,
    vipChannel: recordParams.vipChannel ?? true,
  };

  if (mode === "reference") {
    // 全能参考：imageList + multimodalImageUrls 都可能存在
    const imgUrls: string[] = recordParams.imageList || recordParams.multimodalImageUrls || [];
    patch.referenceImages = imgUrls
      .filter(Boolean)
      .map((url: string, i: number) => ({
        id: `reuse-img-${i}`,
        image: url,
        width: 0,
        height: 0,
      }));

    // 复用场景：优先使用已保存的参考视频总时长，确保算力明细立即正确显示
    const savedInputDuration = Number(recordParams.inputVideoDuration) || 0;
    const videoUrls: string[] = recordParams.videos || recordParams.multimodalVideoUrls || [];
    patch.referenceVideos = videoUrls
      .filter(Boolean)
      .map((url: string, i: number) => ({
        id: `reuse-video-${i}`,
        videoUrl: url,
        // 算力计算按所有参考视频的总时长计费，将保存的总时长赋给第一个视频。
        // 后续 async watcher 会逐个加载实际视频时长进行精确覆盖。
        duration: i === 0 ? savedInputDuration : 0,
        size: 0,
      }));
  } else if (mode === "image-to-video") {
    const imgUrls: string[] = recordParams.imageList || recordParams.multimodalImageUrls || [];
    patch.referenceImages = imgUrls
      .filter(Boolean)
      .map((url: string, i: number) => ({
        id: `reuse-img-${i}`,
        image: url,
        width: 0,
        height: 0,
      }));
    patch.referenceVideos = [];
    patch.firstFrame = null;
    patch.lastFrame = null;
  } else if (mode === "start-end") {
    patch.firstFrame = recordParams.firstImageUrl
      ? { id: "reuse-first", image: recordParams.firstImageUrl, width: 0, height: 0 }
      : null;
    patch.lastFrame = recordParams.lastImageUrl
      ? { id: "reuse-last", image: recordParams.lastImageUrl, width: 0, height: 0 }
      : null;
    patch.referenceImages = [];
    patch.referenceVideos = [];
  } else if (mode === "text-to-video") {
    patch.referenceImages = [];
    patch.referenceVideos = [];
    patch.firstFrame = null;
    patch.lastFrame = null;
  }

  return patch;
}
